// app/frontend/pages/Home/Index.tsx
import { useRef, useEffect } from 'react';
import { Link } from '@inertiajs/inertia-react';
import {
  Container,
  TextInput,
  Title,
  Box,
  Paper,
  Group,
  Button,
  AppShell,
  MantineTheme,
  MantineProvider,
  SimpleGrid,
  Card,
  Text,
  Image,
  Flex,
  ScrollArea,
  ActionIcon,
  rem,
  Skeleton,
} from '@mantine/core';
import { IconSearch, IconChevronLeft, IconChevronRight, IconInfoCircle } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import atxSkyline from '@/assets/images/atx-skyline.jpg';
import yegSkyline from '@/assets/images/edmonton-skyline.jpg';
import phxSkyline from '@/assets/images/phx-skyline.jpg';
import baySkyline from '@/assets/images/bay-skyline.jpg';
import seattleSkyline from '@/assets/images/seattle-skyline.jpg';
import mtlSkyline from '@/assets/images/mtl-skyline.jpg';
import wcsFestival from '@/assets/images/wcs-festival-small.jpg';
import retreatImage from '@/assets/images/retreat-category-image.jpg';
import classImage from '@/assets/images/class-category-image.jpg';
import socialImage from '@/assets/images/social-category-image.jpg';
import weekenderImage from '@/assets/images/weekender-category-image.png';
import compImage from '@/assets/images/comp-category-image.png';
import heroImage from '@/assets/images/hero-image-3.png';

// Define props interface
interface HomePageProps {
  heroData: {
    eventName: string;
    photographer: string;
    imageUrl: string;
  };
  eventCount: number;
  artistCount: number;
  starCount: number;
  areaCount: number;
  areas: {
    id: number;
    name: string;
    slug: string;
    image_url: string;
    upcoming_events_count: number;
    flag: string;
  }[];
}

const categoryData = [
  { title: 'Socials', image: socialImage, id: 1 },
  { title: 'Classes', image: classImage, id: 2 },
  { title: 'Weekenders', image: weekenderImage, id: 3 },
  { title: 'Retreats', image: retreatImage, id: 4 },
  { title: 'Festivals', image: wcsFestival, id: 5 },
  { title: 'Jack & Jills', image: compImage, id: 6 },
];

const categoryToEventTypeMap = {
  1: 4, // Socials
  2: 8, // Classes
  3: 3, // Weekenders
  4: 5, // Retreats
  5: 1, // Festivals
  6: 11, // Jack & Jills
};

const featuresData = [
  {
    title: 'All users/dancers can bookmark events to create their own custom dance calendar of interested events!',
    id: 1,
  },
  {
    title:
      'Artists can improve managing their professional artist careers with artist booking portals, creating own their artist page & dance calendar, monetizing through receiving artist stars, and scheduling private lessons across events! Your followers can subscribe to your artist page and receive a weekly email of where you are teaching next!.',
    id: 2,
  },
  {
    title:
      'Organizers can create their own organizer profile with their own calendars to communicate your upcoming events to your followers! Your followers can also subscribe to your organizer page and get a weekly email of your upcoming events!',
    id: 3,
  },
];

const HomePage: React.FC<HomePageProps> = ({ heroData, eventCount, artistCount, starCount, areaCount, areas }) => {
  const starPayout = (starCount * 5).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const statsData = [
    { value: eventCount, label: 'Events to Date', hasTooltip: false, id: 1 },
    { value: starPayout, label: 'In Artist Stars Raised', hasTooltip: true, id: 2 },
    { value: artistCount, label: 'Artists Added', hasTooltip: false, id: 3 },
    { value: areaCount, label: 'Cities Listed', hasTooltip: false, id: 4 },
  ];
  // Helper styles for consistent font family
  const headerStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(24px, 4vw, 40px)',
  };

  const textStyle = {
    fontFamily: 'Poppins, sans-serif',
  };

  const isMobile = useMediaQuery('(max-width: 576px)');
  const isTablet = useMediaQuery('(min-width: 577px) and (max-width: 992px)');

  const getPlaceholderUrl = (title: string) => {
    if (isMobile) return `https://placehold.co/186x105?text=${title}`;
    if (isTablet) return `https://placehold.co/260x150?text=${title}`;
    return `https://placehold.co/268x200?text=${title}`;
  };

  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    console.log('scrollLeft triggered');
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    console.log('scrollRight triggered');
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    console.log('City data received:', areas);
  }, [areas]);

  useEffect(() => {
    console.log('✈️ Received eventCount:', eventCount);
  }, [eventCount]);

  return (
    <MantineProvider>
      {/* Hero Section */}
      <Box
        style={{
          position: 'relative',
          width: '100%',
          height: isMobile ? '90vw' : 'calc(100vw * 9 / 16)',
          maxHeight: '80vh',
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Black overlay */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 1,
          }}
        />

        {/* Content - Vertically Centered */}
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            textAlign: 'center',
            color: 'white',
            width: '100%',
          }}
          p={{
            base: 'md',
            sm: 'xl',
            md: '2rem',
          }}
        >
          {/* Responsive title */}
          <Title
            style={{
              ...headerStyle,
              lineHeight: 1.1,
            }}
            fz={{
              base: 30,
              sm: 40,
              md: 50,
              lg: 60,
            }}
            mb={{
              base: 'md',
              md: '2.25rem',
            }}
          >
            DISCOVER DANCE EVENTS,
            <br />
            WORLDWIDE
          </Title>

          <Link href="/events">
            <Button
              variant="filled"
              size="lg"
              radius="sm"
              mt="sm"
              mb="4rem"
              style={{
                backgroundColor: '#EA580C',
                ...textStyle,
                fontWeight: 500,
              }}
            >
              View {eventCount} Events
            </Button>
          </Link>
        </Box>

        {/* Photo credits - on all screen sizes with 8px bottom padding */}
        <Box
          style={{
            position: 'absolute',
            bottom: '8px',
            left: '20px',
            color: 'white',
            fontSize: 'calc(10px + 0.2vw)', // Responsive font size
            opacity: 0.8,
            fontFamily: 'Poppins, sans-serif',
            zIndex: 2,
            textShadow: '10px 20px 25px rgba(11, 11, 36, 1)',
          }}
        >
          📸: {heroData.photographer}
        </Box>

        <Box
          style={{
            position: 'absolute',
            bottom: '8px',
            right: '20px',
            color: 'white',
            fontSize: 'calc(10px + 0.2vw)', // Responsive font size
            opacity: 0.8,
            fontFamily: 'Poppins, sans-serif',
            zIndex: 2,
            textShadow: '10px 20px 25px rgba(11, 11, 36, 1)',
          }}
        >
          🗓️: {heroData.eventName}
        </Box>
      </Box>

      {/* Main content with gradient background */}
      <Box
        style={{
          background: 'linear-gradient(135deg, #461702 0%, #0F353D 100%)',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        {/* Dance Events Near You Section */}
        <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
          <Title order={1} ta="left" mb="xl" style={headerStyle}>
            DANCE EVENTS NEAR YOU
          </Title>

          <SimpleGrid
            cols={{ base: 2, sm: 3, md: 3 }}
            spacing={{ base: 'sm', sm: 'xl' }}
            style={{ width: '100%', overflowX: 'hidden' }}
          >
            {categoryData.map((category) => (
              <Link
                key={category.id}
                href="/events"
                data={{
                  upcoming_page: 1,
                  past_page: 1,
                  active_tab: 'upcoming',
                  per_page: 20,
                  filter: {
                    event_type_ids: [categoryToEventTypeMap[category.id as keyof typeof categoryToEventTypeMap]],
                  },
                }}
              >
                <Card
                  padding="sm"
                  radius="md"
                  style={{
                    overflow: 'hidden',
                    width: '100%', // Ensure cards take 100% of grid cell width
                    cursor: 'pointer', // Add pointer cursor for better UX
                  }}
                >
                  <Card.Section style={{ position: 'relative', height: isMobile ? 150 : 260, overflow: 'hidden' }}>
                    <Image
                      src={category.image}
                      fallbackSrc={getPlaceholderUrl(category.title)}
                      alt={category.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    {/* Gradient overlay */}
                    <Box
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '65%',
                        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.3), transparent)',
                        zIndex: 1,
                      }}
                    />
                    {/* Optional: Title overlay inside image */}
                    <Box
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        zIndex: 2,
                      }}
                    >
                      <Text
                        fw={600}
                        c="white"
                        style={{
                          fontSize: '1.5rem',
                          fontFamily: 'Poppins, sans-serif',
                          textShadow: '0 0 4px rgba(0,0,0,0.5)',
                        }}
                      >
                        {category.title}
                      </Text>
                    </Box>
                  </Card.Section>
                </Card>
              </Link>
            ))}
          </SimpleGrid>
        </Container>

        {/* What We Do Section */}
        <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
          <Title order={1} ta="left" mb="md" style={headerStyle}>
            WHAT WE DO
          </Title>

          <Text className="mb-4" size="sm" style={textStyle}>
            Our main goal is to make it easier for dancers to find events and for organizers to promote them with
            interconnected calendars for cities, dance styles, artists, organizers, and event types. We are a
            community-driven platform that allows you to add events and share them with the world.
          </Text>

          <Flex gap="xl" justify="space-between" align="flex-start" direction={{ base: 'column', md: 'row' }}>
            <Box style={{ flex: 1 }}>
              {featuresData.map((feature) => (
                <Box
                  key={feature.id}
                  mb="md"
                  p="lg"
                  style={{
                    borderRadius: '8px',
                    backgroundColor: feature.id === 1 ? '#EA580C' : 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Text style={textStyle}>{feature.title}</Text>
                </Box>
              ))}
            </Box>

            <Box
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {/* Phone Image Placeholder */}
              {/* <Image src="/api/placeholder/320/600" alt="Mobile App Preview" width={320} radius="md" /> */}
            </Box>
          </Flex>
        </Container>

        {/* Our Journey Today Section */}
        <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
          <Title size={40} ta="left" mb="md" style={headerStyle}>
            OUR JOURNEY TODAY
          </Title>

          <Text mb="xl" size="sm" style={textStyle}>
            Since 2022, our team has expanded its network to all parts of the world. We are always expanding and need
            your help in building up the global dance community.
          </Text>

          <Container size="xl" py="xl" style={{ paddingLeft: '0' }}>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
              {statsData.map((stat) => (
                <Box
                  key={stat.id}
                  p="md"
                  style={{
                    borderLeft: `4px solid ${stat.id === 1 || isMobile ? '#EA580C' : 'transparent'}`,
                    paddingLeft: '16px',
                    backgroundColor: '#EA580C20',
                  }}
                >
                  <Text
                    size="xl"
                    fw={700}
                    className="stat-value"
                    style={{
                      ...headerStyle,
                      fontSize: 'clamp(40px, 5vw, 72px)',
                      color: stat.id === 1 ? '#EA580C' : 'white',
                      lineHeight: 1,
                      paddingBottom: '0px',
                    }}
                  >
                    {stat.value}
                  </Text>
                  <Group gap="xs">
                    <Text style={{ fontFamily: 'Poppins, sans-serif' }}>{stat.label}</Text>
                    {stat.hasTooltip && (
                      <ActionIcon variant="subtle" color="gray" size="sm">
                        <IconInfoCircle size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Box>
              ))}
            </SimpleGrid>
          </Container>
        </Container>

        {/* Global Cities Section */}
        <Container size="xl" py={isMobile ? '2rem' : '4rem'}>
          <Flex justify="space-between" align="center" mb="xl">
            <Title order={1} style={headerStyle}>
              FEATURED CITIES
            </Title>
            <Group>
              <ActionIcon variant="filled" color="gray" radius="xl" onClick={scrollLeft}>
                <IconChevronLeft size={18} />
              </ActionIcon>
              <ActionIcon variant="filled" color="gray" radius="xl" onClick={scrollRight}>
                <IconChevronRight size={18} />
              </ActionIcon>
            </Group>
          </Flex>

          <ScrollArea viewportRef={scrollRef} style={{ width: '100%', overflowX: 'hidden' }} type="always">
            <div ref={scrollRef}>
              <Flex gap="md" wrap="nowrap">
                {areas.map((city) => (
                  <Link key={city.id} href={`/city/${city.slug}`}>
                    <Card
                      padding="0"
                      radius="md"
                      style={{
                        position: 'relative',
                        minWidth: 280,
                        maxWidth: 280,
                        cursor: 'pointer',
                      }}
                    >
                      <Card.Section style={{ position: 'relative', height: 360, overflow: 'hidden' }}>
                        <Image
                          src={city.image_url}
                          fallbackSrc="https://placehold.co/352x466?text=Placeholder"
                          alt={city.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        {/* Gradient overlay */}
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '50%',
                            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.3), transparent)',
                          }}
                        />
                        {/* Text overlay */}
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: '16px',
                            zIndex: 2,
                          }}
                        >
                          <Text c="white" fw={700} size="sm" style={textStyle} className="poppins">
                            {city.name} {city.flag}
                          </Text>
                          <Text c="white" size="sm" style={textStyle}>
                            {city.upcoming_events_count} events
                          </Text>
                        </Box>
                      </Card.Section>
                    </Card>
                  </Link>
                ))}
              </Flex>
            </div>
          </ScrollArea>
        </Container>

        {/* Call to Action Section */}
        <Container size="xl" py={isMobile ? '2rem' : '4rem'} style={{ textAlign: 'left' }}>
          <Title order={1} style={headerStyle} mb="md">
            OUR JOURNEY
          </Title>

          <Text maw={700} mx="auto" mb="xl" style={textStyle}>
            Since 2022, our team has come together to try. Since 2023, our team has come together to yet. Since 2024,
            our team has come together to set. Let's continue our journey for every event updates.
          </Text>

          <Link href="/events">
            <Button
              variant="filled"
              size="lg"
              radius="sm"
              mt="sm"
              mb="4rem"
              style={{
                backgroundColor: '#EA580C',
                ...textStyle,
                fontWeight: 500,
              }}
            >
              Add New Event
            </Button>
          </Link>
        </Container>
      </Box>
    </MantineProvider>
  );
};

export default HomePage;
